import React from "react";
import {
    Box,
    Button,
    Pagination,
    Typography,
    useMediaQuery,
    useTheme,
    SxProps,
    Theme,
} from "@mui/material";
import { useTranslation } from "react-i18next";

interface ResponsivePaginationProps {
    page: number;
    pageSize: number;
    totalCount: number;
    onPageChange: (page: number) => void;
    sx?: SxProps<Theme>;
}

const ResponsivePagination: React.FC<ResponsivePaginationProps> = ({
    page,
    pageSize,
    totalCount,
    onPageChange,
    sx,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const { t } = useTranslation();

    const safePageSize = pageSize > 0 ? pageSize : 1;
    const totalPages = Math.max(1, Math.ceil(totalCount / safePageSize));
    const isFirstPage = page <= 0;
    const isLastPage = page + 1 >= totalPages;

    const additionalSx = Array.isArray(sx) ? sx : sx ? [sx] : [];

    if (isMobile) {
        return (
            <Box
                sx={[
                    {
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        justifyContent: { xs: "center", sm: "space-between" },
                        alignItems: "center",
                        mt: 2,
                        gap: 1,
                    },
                    ...additionalSx,
                ]}
            >
                <Button
                    disabled={isFirstPage}
                    variant="contained"
                    onClick={() => {
                        if (!isFirstPage) {
                            onPageChange(page - 1);
                        }
                    }}
                    sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                    {t("common.previous")}
                </Button>

                <Typography
                    sx={{
                        textAlign: "center",
                        width: { xs: "100%", sm: "auto" },
                        whiteSpace: "nowrap",
                    }}
                >
                    {`${t("common.page")} ${Math.min(page + 1, totalPages)}/${totalPages}`}
                </Typography>

                <Button
                    disabled={isLastPage}
                    variant="contained"
                    onClick={() => {
                        if (!isLastPage) {
                            onPageChange(page + 1);
                        }
                    }}
                    sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                    {t("common.next")}
                </Button>
            </Box>
        );
    }

    return (
        <Box
            sx={[
                { display: "flex", justifyContent: "center" },
                ...additionalSx,
            ]}
        >
            <Pagination
                count={totalPages}
                page={page + 1}
                onChange={(_, value) => onPageChange(value - 1)}
                color="primary"
                variant="text" shape="rounded"
            />
        </Box>
    );
};

export default ResponsivePagination;
